import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { PRODUCT_TYPES } from '~/lib/constants'

import BlurredCta from '~/components/UI/BlurredCta'
import { ProductHeaderSubtitle } from '~/components/UI/ProductHeader/HeaderSubtitle'
import LabelCustomization from '~/components/UI/ProductVariantSelector/LabelCustomization'
import VariantsGrid from '~/components/UI/ProductVariantSelector/VariantsGrid'

import { useProductHeaderProvider } from '~/providers/ProductPageProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useUserSelectionProvider } from '~/providers/UserSelectionProvider'

import { useGetStateInformationsFromUserSelection } from '~/hooks/useGetStateInformationsFromUserSelection'
import useLocale from '~/hooks/useLocale'

import { CUSTOMIZER } from '~/data/dictionary'
import serializePrice from '~/data/serialize-price'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface ByCharlotVariantSelectorProps {
  className?: string
  isGiftCard?: boolean
  showImageVariants?: boolean
}
export function ByCharlotVariantSelector({
  className,
  isGiftCard,
  showImageVariants,
}: ByCharlotVariantSelectorProps) {
  const { setUserSelection } = useUserSelectionProvider()
  const { isBundle, setLabelCustomization } = useProductHeaderProvider()
  const t = useTranslate()
  const locale = useLocale()

  const {
    activeTabIndex,
    activeTab,
    variantId,
    variants,
    currentVariant,
    customizerProducts,
    product,
    labelProducts,
    isDisabledEngraving,
    isDisabledLabelCustomization,
  } = useGetStateInformationsFromUserSelection()

  // Here we are setting user selection of customizer product variants
  const handleSelectCustomizerProduct = (customizerProduct, required) => {
    setUserSelection((prevState) => {
      const newState = prevState
      const customizerType = customizerProduct.productType
      const currentVariant = prevState[activeTabIndex][customizerType]
      const shouldReset =
        currentVariant === customizerProduct?.activeVariant?.id && !required

      newState[activeTabIndex][customizerType] = shouldReset
        ? null
        : customizerProduct?.activeVariant?.id
      return [...newState]
    })
  }

  const handleLabelCustomizationUpdate = (obj) => {
    setUserSelection((prev) => {
      const newState = prev

      newState[activeTabIndex].labelCustomization = obj?.product?.id
      newState[activeTabIndex].labelCustomizationVariant = obj?.variant?.id

      return [...newState]
    })

    setLabelCustomization(() => {
      return {
        title: obj?.title ?? '',
        subtitle: obj?.subtitle ?? '',
        computeSubtitleFirst: obj?.computeSubtitleFirst,
      }
    })
  }

  const handleActiveLabel = (obj) => {
    setUserSelection((prev) => {
      const newState = prev

      if (prev[activeTabIndex]?.labelCustomization === obj?.id) {
        newState[activeTabIndex].labelCustomization = null
        newState[activeTabIndex].labelCustomizationVariant = null
      } else {
        newState[activeTabIndex].labelCustomization = obj?.id
        newState[activeTabIndex].labelCustomizationVariant = obj?.variant
      }
      return [...newState]
    })
  }

  const handleVariant = (product, id) => {
    setUserSelection((prevState) => {
      const newState = prevState
      newState[activeTabIndex].variantId = product?.activeVariant?.id
      return [...newState]
    })
  }

  const potVariant =
    customizerProducts
      ?.find((product) => product?.productType === PRODUCT_TYPES.POT)
      ?.variants?.find(
        (variant) => variant?.id === activeTab[PRODUCT_TYPES.POT],
      ) ?? null

  const disabledSaucer = potVariant?.disablesSaucer ?? false

  useEffect(() => {
    if (disabledSaucer) {
      setTimeout(() => {
        setUserSelection((prevState) => {
          const newState = prevState
          newState[activeTabIndex][PRODUCT_TYPES.SAUCER] = null

          return newState
        })
      }, 100)
    }
  }, [disabledSaucer])

  const nameStyle = useStyle({
    textPreset: GlobalTextPreset.Cta12Primary,
    textStyling: GlobalTextStyling.UpperCase,
    color: GlobalThemeColors.Gray1ToBlack,
  })

  const borderStyle = useStyle({
    borderColor: GlobalThemeColors.Gray3ToBlack,
  })

  const [_, setActiveLabel] = useState(activeTab?.labelCustomization)
  useEffect(() => {
    setActiveLabel(activeTab?.labelCustomization)
  }, [activeTab?.labelCustomization])

  return (
    <div className={cx(css.ProductVariantSelector, className)}>
      {!isBundle &&
        !isGiftCard &&
        showImageVariants &&
        variants?.length > 1 && (
          <VariantsGrid
            className={cx(css.customProductItem)}
            currentVariant={variantId}
            required={true}
            title={null}
            withPriceDifference={false}
            onClick={handleVariant}
            product={product}
          />
        )}
      {customizerProducts?.length > 0 && (
        <div className={cx(css.customProductsContainer)}>
          <ProductHeaderSubtitle
            className={css.subtitle}
            text={t(
              CUSTOMIZER.SELECT_PRODUCT(customizerProducts?.[0]?.productType),
            )}
          />

          {customizerProducts?.map((product, index) => {
            return (
              ((product.productType === PRODUCT_TYPES.SAUCER &&
                !disabledSaucer) ||
                product.productType !== PRODUCT_TYPES.SAUCER) && (
                <VariantsGrid
                  key={`customization_product_${index}`}
                  className={cx(css.customProductItem)}
                  currentVariant={activeTab?.[product.productType]}
                  required={product?.required ?? false}
                  title={
                    index > 0
                      ? t(CUSTOMIZER.ADD_PRODUCT(product?.productType))
                      : null
                  }
                  onClick={handleSelectCustomizerProduct}
                  product={product}
                />
              )
            )
          })}
        </div>
      )}
      {(!isDisabledEngraving || !isDisabledLabelCustomization) &&
        labelProducts?.length > 0 && (
          <div className={cx(css.customizationsContainer)}>
            <ProductHeaderSubtitle
              className={css.subtitle}
              text={t(
                CUSTOMIZER.PERSONALIZE_PRODUCT(
                  customizerProducts?.[0]?.productType,
                ),
              )}
            />
            {labelProducts.map((product, i) => {
              return (
                <LabelCustomization
                  key={`customization_${i}`}
                  className={cx(css.labelCustomization, borderStyle)}
                  product={product}
                  disabled={
                    (product.productType === PRODUCT_TYPES.ENGRAVING &&
                      isDisabledEngraving) ||
                    (product.productType === PRODUCT_TYPES.LABEL &&
                      isDisabledLabelCustomization)
                  }
                  active={activeTab?.labelCustomization === product?.id}
                  onChange={handleLabelCustomizationUpdate}
                  click={() => {
                    handleActiveLabel({
                      id: product?.shopifyId,
                      variant: product?.defaultVariant?.id,
                    })
                  }}
                />
              )
            })}
          </div>
        )}
      {((!isBundle && !showImageVariants) || isGiftCard) &&
        variants?.length > 1 && (
          <div className={cx(css.simpleVariantSelector, { isGiftCard })}>
            <span className={cx(css.optionName, nameStyle)}>
              {variants?.[0]?.selectedOptions?.[0]?.name ?? t(CUSTOMIZER.NONE)}
            </span>
            {variants?.map((item) => (
              <BlurredCta
                key={item?.id}
                className={cx(css.variantButton, {
                  [css.active]: item?.id === currentVariant?.id,
                })}
                onClick={() => {
                  setUserSelection((prevState) => {
                    const newState = prevState
                    newState[activeTabIndex].variantId = item?.id
                    return [...newState]
                  })
                }}>
                {isGiftCard
                  ? serializePrice(
                      locale,
                      item?.price?.currencyCode,
                      item?.price?.amount,
                    )
                  : item?.title}
              </BlurredCta>
            ))}
          </div>
        )}
    </div>
  )
}
